import { ListItem } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/service.pb';
import {
  Scope_Enum,
  Setting,
  SettingType_Enum,
} from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { AssetType } from '@weave/schema-gen-ts/dist/shared/organization/asset.pb';
import { camelCase } from 'lodash-es';
import { useQueryClient } from 'react-query';
import { updateObjectFromFieldMask } from '@frontend/schema-field-mask';
import { DEFAULT_SETTINGS } from '../constants';
import { getGetQueryKey, getListQueryKey } from '../queries';
import { GetIO, ListIO, SetIO } from '../types';

type UpdateSettingArgs = Pick<SetIO['input'], 'setting' | 'mask'> & GetIO['input'];
type UseMessagingSettingUpdaterResult = (...updates: UpdateSettingArgs[]) => (() => void) | undefined;
type GetQueryKey = ReturnType<typeof getGetQueryKey>;
type ListQueryKey = ReturnType<typeof getListQueryKey>;
type AggregateQueryUpdates<T extends GetQueryKey | ListQueryKey> = {
  queryKey: T;
  oldData: T extends GetQueryKey ? GetIO['output'] : ListIO['output'];
  updates: UpdateSettingArgs[];
};

export const useMessagingSettingUpdater = (): UseMessagingSettingUpdaterResult => {
  const queryClient = useQueryClient();

  /**
   * A function that takes any number of updates to messaging settings and update the query cache with the final
   * result of all updates
   */
  const updaterFn: UseMessagingSettingUpdaterResult = (...updates) => {
    if (updates.length === 0) return;

    // 1. Collect all queries that could be affected
    const allGetQueries = queryClient.getQueriesData<GetIO['output']>({
      queryKey: getGetQueryKey(),
      exact: false,
    }) as [GetQueryKey, GetIO['output'] | undefined][];
    const listQueriesToInvalidate: ReturnType<typeof getListQueryKey>[] = [];
    const allListQueries = queryClient.getQueriesData<ListIO['output']>({
      queryKey: getListQueryKey(),
      exact: false,
      predicate: ({ state, queryKey }) => {
        const typedQueryKey = queryKey as ReturnType<typeof getListQueryKey>;
        const request = queryKey[2] as ReturnType<typeof getListQueryKey>[2] | undefined;
        const queryData = state.data as ListIO['output'] | undefined;
        if (
          !!request?.locationIds?.length &&
          (queryData?.settings.length ?? -1) < request.locationIds.length &&
          updates.some((update) => update.settingType === request.settingType)
        ) {
          listQueriesToInvalidate.push(typedQueryKey);
          return false;
        }
        return request ? updates.some((update) => update.settingType === request.settingType) : true;
      },
    }) as [ListQueryKey, ListIO['output'] | undefined][];

    // 2. Map each query to the updates that affect it
    const allGetQueriesUpdates = allGetQueries.reduce<AggregateQueryUpdates<GetQueryKey>[]>(
      (acc, [queryKey, queryData]) => {
        const queryRequest = queryKey[2];

        if (!queryRequest || !queryData) return acc;

        const applicableUpdates = updates.filter((update) => {
          if (update.assetId && update.assetId !== queryRequest.assetId) return false;
          else if (update.userId && update.userId !== queryRequest.userId) return false;
          else if (update.locationId && update.locationId !== queryRequest.locationId) return false;
          else if (update.orgId && update.orgId !== queryRequest.orgId) return false;
          else if (update.settingType !== queryRequest.settingType) return false;
          else return true;
        });

        if (applicableUpdates.length === 0) return acc;
        acc.push({
          queryKey,
          // Create deep clone for reversion
          oldData: JSON.parse(JSON.stringify(queryData)) as GetIO['output'],
          updates: applicableUpdates,
        });
        return acc;
      },
      []
    );
    const allListQueriesUpdates = allListQueries.reduce<AggregateQueryUpdates<ListQueryKey>[]>(
      (acc, [queryKey, queryData]) => {
        const queryRequest = queryKey[2];

        if (!queryRequest || !queryData) return acc;

        const applicableUpdates = updates.filter((update) => {
          if (update.assetId && !queryRequest.assetIds?.includes(update.assetId)) return false;
          else if (update.userId && update.userId !== queryRequest.userId) return false;
          else if (update.locationId && !queryRequest.locationIds?.includes(update.locationId)) return false;
          else if (update.orgId && update.orgId !== queryRequest.orgId) return false;
          else if (update.settingType !== queryRequest.settingType) return false;
          else return true;
        });

        if (applicableUpdates.length === 0) return acc;
        acc.push({
          queryKey,
          // Create deep clone for reversion
          oldData: JSON.parse(JSON.stringify(queryData)) as ListIO['output'],
          updates: applicableUpdates,
        });
        return acc;
      },
      []
    );

    // 3. Create objects for all queries with their updated data from each entry in the `updates` array
    const updatedGetQueries = allGetQueriesUpdates.map<{ queryKey: GetQueryKey; updatedData: GetIO['output'] }>(
      ({ queryKey, oldData, updates }) => {
        // Create deep clone
        const updatedData: GetIO['output'] = JSON.parse(JSON.stringify(oldData));

        updates.forEach((update) => {
          updatedData['setting'] = update.mask
            ? updateObjectFromFieldMask<Setting>({
                originalObj: updatedData.setting,
                newValues: update.setting,
                fieldMask: update.mask,
                transformPathKeys: camelCase,
              })
            : update.setting;
        });

        return {
          queryKey,
          updatedData,
        };
      }
    );
    const updatedListQueries = allListQueriesUpdates.map<{ queryKey: ListQueryKey; updatedData: ListIO['output'] }>(
      ({ queryKey, oldData, updates }) => {
        // Create deep clone
        const updatedData: ListIO['output'] = JSON.parse(JSON.stringify(oldData));

        let madeUpdate = false;
        updates.forEach((update) => {
          updatedData['settings'] = updatedData.settings.map<ListIO['output']['settings'][number]>((listItem) => {
            let isApplicable = true;
            if (update.assetId && listItem.assetId !== update.assetId) isApplicable = false;
            else if (update.userId && listItem.userId !== update.userId) isApplicable = false;
            else if (update.locationId && listItem.locationId !== update.locationId) isApplicable = false;
            else if (update.orgId && listItem.orgId !== update.orgId) isApplicable = false;

            if (!isApplicable) return listItem;
            madeUpdate = true;

            return {
              ...listItem,
              setting: update.mask
                ? updateObjectFromFieldMask<Setting>({
                    originalObj: listItem.setting,
                    newValues: update.setting,
                    fieldMask: update.mask,
                    transformPathKeys: camelCase,
                  })
                : update.setting,
            };
          });
        });

        if (!madeUpdate) {
          const getScope = (settingType: SettingType_Enum): Scope_Enum => {
            switch (settingType) {
              case SettingType_Enum.INBOX_LOCATION_USER_SETTING:
              case SettingType_Enum.REPUTATION_NOTIFICATIONS_LOCATION_USER_SETTING:
                return Scope_Enum.LOCATION_USER;
              case SettingType_Enum.INBOX_USER_SETTING:
              case SettingType_Enum.REPUTATION_USER_SETTING:
                return Scope_Enum.USER;
              case SettingType_Enum.INBOX_LOCATION_SETTING:
              case SettingType_Enum.AUTO_MESSAGING_LOCATION_SETTING:
                return Scope_Enum.LOCATION;
              default:
                return Scope_Enum.ORG;
            }
          };

          updatedData['settings'] = [
            ...updatedData.settings,
            ...updates.map<ListItem>((update) => ({
              locationId: update.locationId ?? '',
              assetId: update.assetId ?? '',
              orgId: update.orgId,
              userId: update.userId ?? '',
              modifiedAt: new Date().toISOString(),
              modifiedBy: update.userId ?? '',
              setting: update.mask
                ? updateObjectFromFieldMask<Setting>({
                    originalObj: DEFAULT_SETTINGS,
                    newValues: update.setting,
                    fieldMask: update.mask,
                    transformPathKeys: camelCase,
                  })
                : update.setting,
              settingType: update.settingType,
              assetType: AssetType.ASSET_TYPE_INVALID,
              scope: getScope(update.settingType),
            })),
          ];
        }

        return { queryKey, updatedData };
      }
    );

    // 4. Set each query with the finalized data
    updatedGetQueries.forEach(({ queryKey, updatedData }) => {
      queryClient.setQueryData<GetIO['output']>(queryKey, updatedData);
    });
    updatedListQueries.forEach(({ queryKey, updatedData }) => {
      queryClient.setQueryData<ListIO['output']>(queryKey, updatedData);
    });
    listQueriesToInvalidate.forEach((queryKey) => {
      queryClient.invalidateQueries({ queryKey, exact: true });
    });

    // 5. Return a function that reverts all changes
    const revertFn = () => {
      allGetQueriesUpdates.forEach(({ queryKey, oldData }) => {
        queryClient.setQueryData<GetIO['output']>(queryKey, oldData);
      });
      allListQueriesUpdates.forEach(({ queryKey, oldData }) => {
        queryClient.setQueryData<ListIO['output']>(queryKey, oldData);
      });
    };

    return revertFn;
  };

  return updaterFn;
};
